/**
 * Coerce a heterogeneously-typed date value to a JavaScript `Date`.
 *
 * Firestore documents are returned in different shapes depending on how
 * they're read:
 *  - Firestore SDK `Timestamp` instance (has `.toDate()` method)
 *  - `{ seconds: number, nanoseconds?: number }` plain object (after
 *    JSON serialisation / custom converters)
 *  - `Date` instance (when a converter has already mapped the field)
 *  - ISO string / epoch number (when data was written by older clients)
 *
 * Rather than have every caller re-implement the same union check, this
 * helper folds all those shapes into `Date`.
 *
 * Returns `null` for `null` / `undefined` / unrecognised shapes, so
 * callers can keep the guard explicit: `const d = toDate(x); if (!d) return ...`.
 */
export type DateLike =
  | Date
  | string
  | number
  | { toDate: () => Date }
  | { seconds: number; nanoseconds?: number }
  | { _seconds: number; _nanoseconds?: number };

export function toDate(value: unknown): Date | null {
  if (value === null || value === undefined || value === '') return null;

  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }

  if (typeof value === 'object' && value !== null) {
    const obj = value as { toDate?: unknown; seconds?: unknown; _seconds?: unknown };

    if (typeof obj.toDate === 'function') {
      try {
        const d = (obj as { toDate: () => Date }).toDate();
        return d instanceof Date && !isNaN(d.getTime()) ? d : null;
      } catch {
        return null;
      }
    }

    if (typeof obj.seconds === 'number') {
      const d = new Date((obj as { seconds: number }).seconds * 1000);
      return isNaN(d.getTime()) ? null : d;
    }

    // Firebase Admin SDK serialises Timestamps with leading underscores
    // (`{ _seconds, _nanoseconds }`) when read via Cloud Functions.
    if (typeof obj._seconds === 'number') {
      const d = new Date((obj as { _seconds: number })._seconds * 1000);
      return isNaN(d.getTime()) ? null : d;
    }
  }

  return null;
}

/**
 * Like `toDate` but returns a numeric timestamp (milliseconds since epoch)
 * or `null` when the input can't be coerced. Useful for sort comparisons.
 */
export function toTimestamp(value: unknown): number | null {
  const d = toDate(value);
  return d ? d.getTime() : null;
}
